import { z } from 'zod';
import type { IHttpError } from '@wix/http-client';
import { HttpClient } from '@wix/http-client';
import { CliError, CliErrorCode } from '@wix/cli-error';

// https://github.com/wix-private/server-infra/blob/122c90deb7dbbcf68775c8960c1f8c02a3d7b28c/framework/protos/src/main/proto/wix/api/errors.proto#L12
type HttpErrorResponse =
  | {
      message?: string;
      error?: string;
      details?: {
        applicationError?: object;
        validationError?: object;
      };
    }
  | undefined;

export function isHttpError(
  error: unknown
): error is IHttpError<HttpErrorResponse> {
  return HttpClient.isHttpError(error);
}

const validationErrorSchema = z.object({
  message: z.string().optional(),
  details: z.object({
    validationError: z.object({
      fieldViolations: z
        .array(
          z.object({
            description: z.string(),
            field: z.string(),
          })
        )
        .nonempty(),
    }),
  }),
});

export type HttpValidationErrorResponse = z.infer<typeof validationErrorSchema>;

export function isHttpValidationError(
  error: IHttpError<HttpErrorResponse>
): error is IHttpError<HttpValidationErrorResponse> {
  if (error.response?.data?.details?.validationError) {
    const result = validationErrorSchema.safeParse(error.response.data);

    if (result.success) {
      return true;
    }

    throw new CliError({
      cause: error,
      code: CliErrorCode.FailedToParseHttpErrorResponse(),
      info: {
        zodError: result.error,
      },
    });
  }

  return false;
}

const applicationErrorSchema = z.object({
  message: z.string().optional(),
  details: z.object({
    applicationError: z.object({
      code: z.string(),
      description: z.string().optional(),
      data: z
        .object({
          appNotInstalled: z
            .object({
              appId: z.string(),
            })
            .optional(),
        })
        .optional(),
    }),
  }),
});

export type HttpApplicationErrorResponse = z.infer<
  typeof applicationErrorSchema
>;

export function isHttpApplicationError(
  error: IHttpError<HttpErrorResponse>
): error is IHttpError<HttpApplicationErrorResponse> {
  if (error.response?.data?.details?.applicationError) {
    const result = applicationErrorSchema.safeParse(error.response.data);

    if (result.success) {
      return true;
    }

    throw new CliError({
      cause: error,
      code: CliErrorCode.FailedToParseHttpErrorResponse(),
      info: {
        zodError: result.error,
      },
    });
  }

  return false;
}
